import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import NotificationCenter from "../../ui/NotificationCenter";

export const DashboardTab = ({
  dashboardStats,
  dashboardFilter,
  setDashboardFilter,
  setGlobalFilters,
  clearGlobalFilters,
  updateGlobalFilter,
  refreshDashboard,
  setActiveTab,
  expandedAssetId,
  setExpandedAssetId,
  assetInventory,
}) => {
  const navigate = useNavigate();

  const selectedAssetDetail = useMemo(() => {
    if (!dashboardStats || !expandedAssetId) {
      return null;
    }

    const assetInventoryData = assetInventory.length
      ? assetInventory
      : dashboardStats.asset_inventory || [];

    return (
      assetInventoryData.find((asset) => {
        const assetId =
          asset.asset_id || `${asset.asset_name}-${asset.provider}`;
        return assetId === expandedAssetId;
      }) || null
    );
  }, [dashboardStats, expandedAssetId, assetInventory]);

  if (!dashboardStats) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
        No asset intelligence available yet.
      </div>
    );
  }

  const assetInventoryData = dashboardStats.asset_inventory || [];
  const all = assetInventoryData.length;
  const sensitive = assetInventoryData.filter(
    (asset) => asset.contains_sensitive_data,
  ).length;
  const publicAssets = assetInventoryData.filter(
    (asset) => asset.public_access,
  ).length;
  const critical = assetInventoryData.filter(
    (asset) => (asset.criticality || "").toLowerCase() === "high",
  ).length;

  const filteredAssets = assetInventoryData.filter((asset) => {
    if (dashboardFilter === "sensitive") return asset.contains_sensitive_data;
    if (dashboardFilter === "public") return asset.public_access;
    if (dashboardFilter === "critical")
      return (asset.criticality || "").toLowerCase() === "high";
    return true;
  });

  const providerDistribution = Object.entries(
    assetInventoryData.reduce((acc, asset) => {
      const provider = asset.provider || "Unknown";
      acc[provider] = (acc[provider] || 0) + 1;
      return acc;
    }, {}),
  )
    .map(([provider, count]) => ({ provider, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const maxProviderCount = Math.max(
    ...providerDistribution.map((item) => item.count),
    1,
  );

  const severityTrend = [
    {
      label: "Critical",
      value: assetInventoryData.filter(
        (asset) => (asset.criticality || "").toLowerCase() === "high",
      ).length,
      color: "bg-slate-600",
    },
    {
      label: "Moderate",
      value: assetInventoryData.filter(
        (asset) => (asset.criticality || "").toLowerCase() === "medium",
      ).length,
      color: "bg-slate-500",
    },
    {
      label: "Low",
      value: assetInventoryData.filter(
        (asset) => (asset.criticality || "").toLowerCase() === "low",
      ).length,
      color: "bg-slate-400",
    },
  ];

  const maxSeverity = Math.max(...severityTrend.map((item) => item.value), 1);

  const fallbackActivity = assetInventoryData
    .slice(0, 5)
    .map((asset, index) => ({
      title: `${asset.asset_name} discovered`,
      detail: `${asset.provider} • ${asset.service || asset.asset_type}`,
      time: `${index + 1}m ago`,
      tone: asset.contains_sensitive_data
        ? "amber"
        : asset.public_access
          ? "rose"
          : "emerald",
    }));

  const activityFeed =
    dashboardStats.recent_activity && dashboardStats.recent_activity.length
      ? dashboardStats.recent_activity
      : fallbackActivity;

  const metricCards = [
    {
      label: "Total assets",
      value: dashboardStats.total_assets ?? all,
      accent: "border-slate-200 bg-slate-50",
      text: "text-slate-900",
      key: "all",
      eventLabel: "All assets",
      summary: "Total monitored assets across all connected environments.",
      severity: null,
      action: () => {
        setDashboardFilter("all");
        clearGlobalFilters();
      },
    },
    {
      label: "Sensitive data",
      value: sensitive,
      accent: "border-slate-200 bg-slate-50",
      text: "text-slate-800",
      key: "sensitive",
      eventLabel: "Sensitive data",
      summary: `${sensitive} assets contain sensitive data and are in scope.`,
      severity: "Watch",
      action: () => {
        setDashboardFilter("sensitive");
        setGlobalFilters((prev) => ({ ...prev, publicExposure: "false" }));
      },
    },
    {
      label: "Public access",
      value: publicAssets,
      accent: "border-slate-200 bg-slate-50",
      text: "text-slate-800",
      key: "public",
      eventLabel: "Public exposure",
      summary: `${publicAssets} assets are publicly reachable and need review.`,
      severity: "Review",
      action: () => {
        setDashboardFilter("public");
        setGlobalFilters((prev) => ({ ...prev, publicExposure: "true" }));
      },
    },
    {
      label: "Critical assets",
      value: critical,
      accent: "border-slate-200 bg-slate-50",
      text: "text-slate-800",
      key: "critical",
      eventLabel: "Critical watchlist",
      summary: `${critical} high-priority assets need review.`,
      severity: "Priority",
      action: () => {
        setDashboardFilter("critical");
        setGlobalFilters((prev) => ({ ...prev, criticality: "high" }));
      },
    },
  ];

  return (
    <>
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="mb-5 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
              Asset command center
            </p>
            <h2 className="mt-2 text-[28px] font-semibold tracking-[-0.02em] text-slate-900">
              Asset intelligence overview
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/command-centre")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-[0_0_0_2px_rgba(148,163,184,0.08)] transition hover:bg-slate-200"
            >
              <span className="h-2 w-2 animate-pulse rounded-full bg-slate-500" />
              AI agent
            </button>
            <button
              type="button"
              onClick={() => navigate("/command-centre")}
              className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-700"
            >
              Open Command Centre
            </button>
          </div>
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-2">
          {[
            {
              label: "Investigate exposure",
              tone: "bg-slate-900 text-white hover:bg-slate-800",
              action: () => {
                setDashboardFilter("public");
                setGlobalFilters((prev) => ({
                  ...prev,
                  publicExposure: "true",
                }));
              },
            },
            {
              label: "Review critical",
              tone: "bg-slate-100 text-slate-800 hover:bg-slate-200",
              action: () => {
                setDashboardFilter("critical");
                setGlobalFilters((prev) => ({ ...prev, criticality: "high" }));
              },
            },
            {
              label: "Open inventory",
              tone: "bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200",
              action: () => setActiveTab("inventory"),
            },
            {
              label: "Refresh now",
              tone: "bg-slate-100 text-slate-700 hover:bg-slate-200",
              action: () => refreshDashboard(),
            },
          ].map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.action}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 hover:-translate-y-0.5 ${action.tone}`}
            >
              {action.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full lg:w-[28rem] flex-shrink-0 h-[380px]">
          <NotificationCenter module="asset_management" title="Asset Management Notifications" />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 flex-1">
          {metricCards.map((card) => (
            <motion.button
              key={card.label}
              type="button"
              whileHover={{ y: -2, scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={card.action}
              className={`group rounded-2xl border ${card.accent} p-4 text-left shadow-sm transition-all duration-200 hover:shadow-md`}
            >
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {card.label}
                </p>
                <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-600">
                  View
                </span>
              </div>
              <p
                className={`mt-4 text-[30px] font-bold tracking-[-0.04em] ${card.text}`}
              >
                {card.value}
              </p>

              <div className="mt-3 max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out group-hover:max-h-24 group-hover:opacity-100">
                <div className="rounded-xl border border-slate-200 bg-white/80 p-3 shadow-sm">
                  {card.severity && (
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700">
                        {card.severity}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-500">
                        {card.value}
                      </span>
                    </div>
                  )}
                  <p className="text-sm font-semibold text-slate-900">
                    {card.eventLabel}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    {card.summary}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-[2.1fr_1fr]">
          <div className="rounded-2xl border border-slate-200 bg-slate-900 p-4 text-white shadow-[0_12px_30px_rgba(15,23,42,0.12)]">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
                  Asset stream
                </p>
                <h3 className="mt-2 text-[18px] font-semibold text-white">
                  Live asset intelligence
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  {
                    id: "all",
                    label: `All (${all})`,
                    active: "bg-white text-slate-900",
                    inactive: "bg-slate-800 text-slate-200 hover:bg-slate-700",
                  },
                  {
                    id: "sensitive",
                    label: `Sensitive (${sensitive})`,
                    active: "bg-white text-slate-900",
                    inactive: "bg-slate-800 text-slate-200 hover:bg-slate-700",
                  },
                  {
                    id: "public",
                    label: `Public (${publicAssets})`,
                    active: "bg-white text-slate-900",
                    inactive: "bg-slate-800 text-slate-200 hover:bg-slate-700",
                  },
                  {
                    id: "critical",
                    label: `Critical (${critical})`,
                    active: "bg-white text-slate-900",
                    inactive: "bg-slate-800 text-slate-200 hover:bg-slate-700",
                  },
                ].map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => {
                      setDashboardFilter(filter.id);
                      if (filter.id === "critical")
                        setGlobalFilters((prev) => ({
                          ...prev,
                          criticality: "high",
                        }));
                      if (filter.id === "public")
                        setGlobalFilters((prev) => ({
                          ...prev,
                          publicExposure: "true",
                        }));
                      if (filter.id === "sensitive")
                        setGlobalFilters((prev) => ({
                          ...prev,
                          publicExposure: "false",
                        }));
                      if (filter.id === "all") clearGlobalFilters();
                    }}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${dashboardFilter === filter.id ? filter.active : filter.inactive}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredAssets.length > 0 ? (
              <div className="max-h-[65vh] space-y-3 overflow-y-auto overscroll-contain pr-2">
                {filteredAssets.map((asset, index) => {
                  const assetId =
                    asset.asset_id || `${asset.asset_name}-${asset.provider}`;
                  const isCritical =
                    (asset.criticality || "").toLowerCase() === "high";

                  return (
                    <motion.button
                      key={`${assetId}-${index}`}
                      type="button"
                      onClick={() => setExpandedAssetId(assetId)}
                      whileHover={{ scale: 1.01, x: 2 }}
                      whileTap={{ scale: 0.99 }}
                      className="block w-full text-left"
                    >
                      <div
                        className={`rounded-2xl border p-4 transition duration-200 ${isCritical ? "border-slate-700 bg-slate-800/90" : "border-slate-700 bg-slate-800/60"}`}
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-lg font-semibold text-white">
                                {asset.asset_name}
                              </span>
                              <span className="rounded-full border border-slate-600 bg-slate-700 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-200">
                                {asset.asset_type}
                              </span>
                              {isCritical && (
                                <span className="inline-flex h-2.5 w-2.5 animate-pulse rounded-full bg-slate-400" />
                              )}
                            </div>
                            <p className="mt-1 text-sm text-slate-300">
                              {asset.service} • {asset.region} •{" "}
                              {asset.provider}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <span className="rounded-full bg-slate-700 px-2.5 py-1 text-[11px] font-medium text-slate-100">
                              {asset.classification}
                            </span>
                            {asset.public_access && (
                              <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-300">
                                Public
                              </span>
                            )}
                            {asset.contains_sensitive_data && (
                              <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-300">
                                Sensitive
                              </span>
                            )}
                            {isCritical && (
                              <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-300">
                                High priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-700 bg-slate-800/80 p-6 text-sm text-slate-300">
                No assets match this dashboard filter yet.
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">
                  Provider split
                </h4>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-700">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" />
                  Live
                </span>
              </div>
              <div className="space-y-3">
                {providerDistribution.length > 0 ? (
                  providerDistribution.map((item) => (
                    <button
                      type="button"
                      key={item.provider}
                      onClick={() =>
                        updateGlobalFilter("provider", item.provider)
                      }
                      className="block w-full text-left"
                    >
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                        <span>{item.provider}</span>
                        <span className="font-semibold">{item.count}</span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-slate-600 to-slate-400"
                          style={{
                            width: `${(item.count / maxProviderCount) * 100}%`,
                          }}
                        />
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    No provider data yet.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">
                  Severity trend
                </h4>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-700">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" />
                  Today
                </span>
              </div>
              <div className="space-y-3">
                {severityTrend.map((item) => (
                  <button
                    type="button"
                    key={item.label}
                    onClick={() =>
                      updateGlobalFilter(
                        "criticality",
                        item.label === "Critical"
                          ? "high"
                          : item.label === "Moderate"
                            ? "medium"
                            : "low",
                      )
                    }
                    className="block w-full text-left"
                  >
                    <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                      <span>{item.label}</span>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${item.color}`}
                        style={{
                          width: `${(item.value / maxSeverity) * 100}%`,
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">
                  Activity
                </h4>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-700">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" />
                  Live
                </span>
              </div>
              <div className="space-y-3">
                {activityFeed.map((activity, index) => (
                  <button
                    key={`${activity.title}-${index}`}
                    type="button"
                    onClick={() => {
                      if (activity.asset_name) {
                        setExpandedAssetId(activity.asset_name);
                      }
                    }}
                    className="flex w-full gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-left transition-colors hover:bg-slate-100"
                  >
                    <div
                      className={`mt-1 h-2.5 w-2.5 rounded-full ${activity.tone === "amber" ? "bg-slate-500" : activity.tone === "rose" ? "bg-slate-500" : "bg-slate-600"}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800">
                        {activity.title}
                      </p>
                      <p className="text-xs text-slate-500">
                        {activity.detail}
                      </p>
                    </div>
                    <span className="text-[10px] font-medium text-slate-400">
                      {activity.time || "now"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selectedAssetDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex justify-end bg-slate-950/30"
            onClick={() => setExpandedAssetId(null)}
          >
            <motion.aside
              initial={{ x: 420 }}
              animate={{ x: 0 }}
              exit={{ x: 420 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-200 bg-white p-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Asset details
                  </p>
                  <h3 className="mt-2 text-2xl font-bold text-slate-900">
                    {selectedAssetDetail.asset_name}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setExpandedAssetId(null)}
                  className="rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
                >
                  Close
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                      {selectedAssetDetail.asset_type}
                    </span>
                    <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {selectedAssetDetail.provider}
                    </span>
                    <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {selectedAssetDetail.region}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                      Classification
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">
                      {selectedAssetDetail.classification || "Unclassified"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                      Criticality
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">
                      {selectedAssetDetail.criticality || "Medium"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                      Public access
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">
                      {selectedAssetDetail.public_access
                        ? "Enabled"
                        : "Restricted"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                      Sensitive data
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">
                      {selectedAssetDetail.contains_sensitive_data
                        ? "Detected"
                        : "Not detected"}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <h4 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">
                    Metadata
                  </h4>
                  <ul className="mt-3 space-y-2 text-sm text-slate-600">
                    <li>
                      <span className="font-medium text-slate-800">
                        Service:
                      </span>{" "}
                      {selectedAssetDetail.service || "N/A"}
                    </li>
                    <li>
                      <span className="font-medium text-slate-800">
                        Account:
                      </span>{" "}
                      {selectedAssetDetail.account_id ||
                        selectedAssetDetail.account ||
                        "N/A"}
                    </li>
                    <li>
                      <span className="font-medium text-slate-800">Owner:</span>{" "}
                      {selectedAssetDetail.owner || "Unassigned"}
                    </li>
                    <li>
                      <span className="font-medium text-slate-800">
                        Last sync:
                      </span>{" "}
                      {selectedAssetDetail.last_sync ||
                        selectedAssetDetail.last_seen ||
                        "Recent"}
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <h4 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">
                    Risk context
                  </h4>
                  <p className="mt-3 text-sm text-slate-600">
                    {selectedAssetDetail.public_access
                      ? "This asset is publicly reachable and should be reviewed for exposure controls."
                      : selectedAssetDetail.contains_sensitive_data
                        ? "This asset contains sensitive data and warrants tighter monitoring and access restrictions."
                        : "This asset appears operationally stable with no immediate exposure signal detected."}
                  </p>
                </div>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
